import { Container, Graphics } from 'pixi.js';
import { Easings } from '../../core/Tween';

/**
 * Attack effects drawn across the whole wheel stage: forked lightning
 * from the attacker to every rival, and impact bursts (ring, flash,
 * sparks) for bomb / hit / pow style hits.
 *
 * <p>Producer 2026-10-06: "make more of the motion graphics on the wheel
 * animations, e.g. the lightning". Before this, every item effect was the
 * same small move (the icon dropped ~36px onto the victim's avatar). The
 * per-seat icon + coin floater ({@code WheelResolveSeatFx}) still play;
 * this layer sits above them and adds the motion.
 *
 * <p>Everything is drawn procedurally (no art), redrawn each frame while
 * an effect runs, and the widget hides itself when idle. The host scene
 * owns screen shake via the {@code onImpact} callbacks.
 */

interface Pt {
  x: number;
  y: number;
}

export interface LightningStrikeOptions {
  from: Pt;
  targets: ReadonlyArray<Pt>;
  viewport: { width: number; height: number };
  /** Chain lightning: each bolt starts where the previous one landed
   *  (source -> target 0 -> target 1 ...) instead of all leaving the
   *  source. The host orders targets around the rim so the hops stay
   *  near the edge, clear of the central ledger. */
  chain?: boolean;
  /** Wheel centre. With {@code chain}, each hop follows an arc just
   *  outside the rim around this point, so the bolts race round the
   *  edge in plain view instead of cutting under the central ledger. */
  center?: Pt;
  /** Fires once per target as its bolt lands, in strike order. */
  onImpact?: (index: number) => void;
}

export interface ImpactBurstOptions {
  at: ReadonlyArray<Pt>;
  /** Ring + spark colour family. */
  palette: 'fire' | 'gold' | 'white';
  /** Delay between bursts when there are several targets. */
  staggerMs?: number;
  /** Bigger burst for heavier hits (bomb). */
  scale?: number;
  onImpact?: (index: number) => void;
}

/** Source glow before the first bolt leaves. */
const CHARGE_MS = 170;
/** Gap between bolts when lightning chains across several rivals. */
const STAGGER_MS = 140;
/** Full-strength, flickering part of each bolt. */
const BOLT_LIVE_MS = 340;
const BOLT_FADE_MS = 260;
/** New bolt shape every FLICKER_MS while live. */
const FLICKER_MS = 55;
const IMPACT_MS = 440;
const FLASH_MS = 280;

const PALETTES = {
  fire: { ring: 0xffa040, core: 0xffe2a0, spark: 0xff6a2a },
  gold: { ring: 0xffd66b, core: 0xfff4c8, spark: 0xffe48a },
  white: { ring: 0xd8f1ff, core: 0xffffff, spark: 0xfff3a0 },
} as const;

export class WheelResolveAttackFx extends Container {
  private readonly flash = new Graphics();
  private readonly glow = new Graphics();
  private readonly core = new Graphics();
  private readonly bursts = new Graphics();
  /** Effects in flight; one animation-frame loop clears the layer and
   *  redraws all of them, so overlapping effects never wipe each other. */
  private active: { startMs: number; durationMs: number; draw: (t: number) => void }[] = [];
  private rafId: number | null = null;

  constructor() {
    super();
    this.eventMode = 'none';
    this.glow.blendMode = 'add';
    this.bursts.blendMode = 'add';
    this.addChild(this.flash, this.glow, this.core, this.bursts);
    this.visible = false;
  }

  /** Chain lightning: charge at the source, then a forked, flickering
   *  bolt to each target in turn with a screen flash and an impact
   *  burst where it lands. */
  strike(opts: LightningStrikeOptions): void {
    const { from, targets, viewport } = opts;
    if (targets.length === 0) return;
    const total =
      CHARGE_MS +
      (targets.length - 1) * STAGGER_MS +
      Math.max(BOLT_LIVE_MS + BOLT_FADE_MS, IMPACT_MS);
    const shapes: Pt[][][] = targets.map(() => []);
    const shapeFrame = targets.map(() => -1);
    const landed = targets.map(() => false);
    const sparks = targets.map(() => sparkAngles(9));
    this.run(total, (t) => {
      // Charge-up glow at the attacker.
      if (t < CHARGE_MS + 140) {
        const c = Math.min(1, t / CHARGE_MS);
        const fade = t > CHARGE_MS ? 1 - (t - CHARGE_MS) / 140 : 1;
        this.bursts
          .circle(from.x, from.y, 18 + 30 * c)
          .fill({ color: 0x7fd0ff, alpha: 0.5 * fade });
        this.bursts
          .circle(from.x, from.y, 9 + 9 * c)
          .fill({ color: 0xffffff, alpha: 0.85 * fade });
      }
      // Screen flash as the first bolt lands, then a softer one per bolt.
      for (let i = 0; i < targets.length; i++) {
        const ft = t - (CHARGE_MS + i * STAGGER_MS);
        if (ft >= 0 && ft < FLASH_MS) {
          const peak = i === 0 ? 0.5 : 0.18;
          this.flash
            .rect(0, 0, viewport.width, viewport.height)
            .fill({ color: 0xdff4ff, alpha: peak * (1 - ft / FLASH_MS) });
        }
      }
      targets.forEach((to, i) => {
        const bt = t - (CHARGE_MS + i * STAGGER_MS);
        if (bt < 0) return;
        const origin = opts.chain && i > 0 ? targets[i - 1]! : from;
        if (!landed[i]) {
          landed[i] = true;
          opts.onImpact?.(i);
        }
        if (bt < BOLT_LIVE_MS + BOLT_FADE_MS) {
          const frame = Math.floor(bt / FLICKER_MS);
          if (bt < BOLT_LIVE_MS && frame !== shapeFrame[i]) {
            shapes[i] = opts.chain && opts.center
              ? rimBolt(origin, to, opts.center)
              : forkedBolt(origin, to);
            shapeFrame[i] = frame;
          }
          const alpha =
            bt < BOLT_LIVE_MS
              ? frame % 2 === 0
                ? 1
                : 0.7
              : 1 - (bt - BOLT_LIVE_MS) / BOLT_FADE_MS;
          shapes[i]!.forEach((path, k) => {
            const main = k === 0;
            strokePath(this.glow, path, main ? 26 : 12, 0x5fb8ff, 0.32 * alpha);
            strokePath(this.glow, path, main ? 11 : 5, 0xb8e6ff, 0.65 * alpha);
            strokePath(this.core, path, main ? 3.6 : 1.8, 0xffffff, alpha);
          });
        }
        if (bt < IMPACT_MS) this.drawBurst(to, bt / IMPACT_MS, sparks[i]!, PALETTES.white, 1);
      });
    });
  }

  /** Impact bursts on one or more points (bomb, hit, pow). */
  burst(opts: ImpactBurstOptions): void {
    const { at } = opts;
    if (at.length === 0) return;
    const stagger = opts.staggerMs ?? 90;
    const scale = opts.scale ?? 1;
    const palette = PALETTES[opts.palette];
    const total = (at.length - 1) * stagger + IMPACT_MS;
    const landed = at.map(() => false);
    const sparks = at.map(() => sparkAngles(scale > 1 ? 12 : 8));
    this.run(total, (t) => {
      at.forEach((p, i) => {
        const bt = t - i * stagger;
        if (bt < 0 || bt >= IMPACT_MS) return;
        if (!landed[i]) {
          landed[i] = true;
          opts.onImpact?.(i);
        }
        this.drawBurst(p, bt / IMPACT_MS, sparks[i]!, palette, scale);
      });
    });
  }

  stop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.active = [];
    this.clearAll();
    this.visible = false;
  }

  destroyDisplay(): void {
    this.stop();
    this.destroy({ children: true });
  }

  // ---- internal ---------------------------------------------------------

  private run(durationMs: number, draw: (elapsedMs: number) => void): void {
    this.active.push({ startMs: performance.now(), durationMs, draw });
    this.visible = true;
    if (this.rafId === null) this.rafId = requestAnimationFrame(this.tick);
  }

  private readonly tick = (now: number): void => {
    this.clearAll();
    this.active = this.active.filter((e) => {
      const t = now - e.startMs;
      if (t >= e.durationMs) return false;
      e.draw(Math.max(0, t));
      return true;
    });
    if (this.active.length > 0) {
      this.rafId = requestAnimationFrame(this.tick);
    } else {
      this.rafId = null;
      this.clearAll();
      this.visible = false;
    }
  };

  private clearAll(): void {
    this.flash.clear();
    this.glow.clear();
    this.core.clear();
    this.bursts.clear();
  }

  private drawBurst(
    at: Pt,
    q: number,
    angles: number[],
    palette: { ring: number; core: number; spark: number },
    scale: number,
  ): void {
    const e = Easings.easeOutCubic(q);
    const fade = 1 - q;
    this.bursts
      .circle(at.x, at.y, (12 + 52 * e) * scale)
      .stroke({ color: palette.ring, alpha: 0.9 * fade, width: (4 * fade + 1) * scale });
    this.bursts
      .circle(at.x, at.y, (26 * fade + 4) * scale)
      .fill({ color: palette.core, alpha: 0.7 * fade });
    for (const a of angles) {
      const r0 = (14 + 34 * e) * scale;
      const r1 = r0 + 14 * fade * scale;
      this.bursts
        .moveTo(at.x + Math.cos(a) * r0, at.y + Math.sin(a) * r0)
        .lineTo(at.x + Math.cos(a) * r1, at.y + Math.sin(a) * r1)
        .stroke({ color: palette.spark, alpha: fade, width: 2.5 * scale, cap: 'round' });
    }
  }
}

function sparkAngles(n: number): number[] {
  return Array.from({ length: n }, (_, k) => (k / n) * Math.PI * 2 + Math.random() * 0.5);
}

/** Main jagged bolt plus one or two short side forks. */
function forkedBolt(a: Pt, b: Pt): Pt[][] {
  const main = jagged(a, b, 0.17, 6);
  const paths = [main];
  const forks = Math.random() < 0.5 ? 2 : 1;
  const dir = Math.atan2(b.y - a.y, b.x - a.x);
  const span = Math.hypot(b.x - a.x, b.y - a.y);
  for (let k = 0; k < forks; k++) {
    const start = main[Math.floor(main.length * (0.25 + Math.random() * 0.45))]!;
    const angle = dir + (Math.random() < 0.5 ? -1 : 1) * (0.35 + Math.random() * 0.5);
    const len = span * (0.15 + Math.random() * 0.2);
    paths.push(
      jagged(start, { x: start.x + Math.cos(angle) * len, y: start.y + Math.sin(angle) * len }, 0.25, 4),
    );
  }
  return paths;
}

/** Bolt that follows an arc around the wheel centre, just outside the
 *  rim, in the direction of increasing angle, plus one short fork
 *  pointing outwards. */
function rimBolt(a: Pt, b: Pt, c: Pt): Pt[][] {
  const angA = Math.atan2(a.y - c.y, a.x - c.x);
  const angB = Math.atan2(b.y - c.y, b.x - c.x);
  const delta = (angB - angA + Math.PI * 4) % (Math.PI * 2);
  const rA = Math.hypot(a.x - c.x, a.y - c.y);
  const rB = Math.hypot(b.x - c.x, b.y - c.y);
  const steps = Math.max(3, Math.ceil(delta / 0.22));
  const arc: Pt[] = [a];
  for (let k = 1; k < steps; k++) {
    const f = k / steps;
    // Bulge outwards mid-hop so the bolt clears the rim.
    const r = (rA + (rB - rA) * f) * (1 + 0.12 * Math.sin(Math.PI * f));
    const ang = angA + delta * f;
    arc.push({ x: c.x + Math.cos(ang) * r, y: c.y + Math.sin(ang) * r });
  }
  arc.push(b);
  const main: Pt[] = [a];
  for (let k = 0; k < arc.length - 1; k++) {
    main.push(...jagged(arc[k]!, arc[k + 1]!, 0.35, 3).slice(1));
  }
  const start = main[Math.floor(main.length * (0.3 + Math.random() * 0.4))]!;
  const out = Math.atan2(start.y - c.y, start.x - c.x) + (Math.random() - 0.5) * 0.8;
  const len = 14 + Math.random() * 16;
  const fork = jagged(start, { x: start.x + Math.cos(out) * len, y: start.y + Math.sin(out) * len }, 0.3, 3);
  return [main, fork];
}

/** Midpoint displacement: each round splits every segment and pushes the
 *  midpoint sideways by a random amount that halves each round. */
function jagged(a: Pt, b: Pt, roughness: number, rounds: number): Pt[] {
  let pts: Pt[] = [a, b];
  let offset = Math.hypot(b.x - a.x, b.y - a.y) * roughness;
  for (let r = 0; r < rounds; r++) {
    const next: Pt[] = [pts[0]!];
    for (let i = 0; i < pts.length - 1; i++) {
      const p = pts[i]!;
      const q = pts[i + 1]!;
      const dx = q.x - p.x;
      const dy = q.y - p.y;
      const len = Math.hypot(dx, dy) || 1;
      const n = (Math.random() * 2 - 1) * offset;
      next.push({ x: (p.x + q.x) / 2 - (dy / len) * n, y: (p.y + q.y) / 2 + (dx / len) * n }, q);
    }
    pts = next;
    offset *= 0.5;
  }
  return pts;
}

function strokePath(g: Graphics, path: Pt[], width: number, color: number, alpha: number): void {
  if (alpha <= 0 || path.length < 2) return;
  g.moveTo(path[0]!.x, path[0]!.y);
  for (let i = 1; i < path.length; i++) g.lineTo(path[i]!.x, path[i]!.y);
  g.stroke({ width, color, alpha, cap: 'round', join: 'round' });
}
